'use client';

import React, { useState, useMemo } from 'react';
import Link from 'next/link';
import { useCommandCenterTool } from '@/lib/command-center/useCommandCenterTool';

interface Pod {
  id: string;
  name: string;
  namespace: string;
  cpuRequest: number; // millicores
  cpuUsage: number; // millicores
  memoryRequest: number; // MB
  memoryUsage: number; // MB;
  replicas: number;
}

interface NodePool {
  name: string;
  instanceType: string;
  vCPUs: number;
  memoryGB: number;
  costPerHour: number;
  count: number;
}

export default function KubernetesCostOptimizerPage() {
  // Pods (free tier limited to single cluster)
  const [pods, setPods] = useState<Pod[]>([
    { id: '1', name: 'api-server', namespace: 'production', cpuRequest: 500, cpuUsage: 200, memoryRequest: 512, memoryUsage: 300, replicas: 3 },
    { id: '2', name: 'worker', namespace: 'production', cpuRequest: 1000, cpuUsage: 800, memoryRequest: 1024, memoryUsage: 900, replicas: 5 },
  ]);

  // Node Pool
  const [nodePool, setNodePool] = useState<NodePool>({
    name: 'default-pool',
    instanceType: 't3.large',
    vCPUs: 2,
    memoryGB: 8,
    costPerHour: 0.0832,
    count: 4,
  });

  // Spot instance savings
  const [useSpotInstances, setUseSpotInstances] = useState(false);
  const [spotSavingsPercent, setSpotSavingsPercent] = useState(70);
  const [saved, setSaved] = useState(false);

  // Command Center integration
  const {
    enabled: commandCenterEnabled,
    saving,
    error: saveError,
    saveResult,
  } = useCommandCenterTool({
    toolId: 'kubernetes-cost-optimizer',
    toolCategory: 'calculator',
  });

  // Calculations
  const calculations = useMemo(() => {
    const validPods = pods.filter(p => p.name.trim());

    // Total resource requests and usage
    const totalCPURequest = validPods.reduce((sum, p) => sum + (p.cpuRequest * p.replicas), 0);
    const totalCPUUsage = validPods.reduce((sum, p) => sum + (p.cpuUsage * p.replicas), 0);
    const totalMemoryRequest = validPods.reduce((sum, p) => sum + (p.memoryRequest * p.replicas), 0);
    const totalMemoryUsage = validPods.reduce((sum, p) => sum + (p.memoryUsage * p.replicas), 0);

    // Node pool capacity
    const totalNodeCPU = nodePool.count * nodePool.vCPUs * 1000; // Convert to millicores
    const totalNodeMemory = nodePool.count * nodePool.memoryGB * 1024; // Convert to MB

    // Utilization
    const cpuRequestUtilization = (totalCPURequest / totalNodeCPU) * 100;
    const cpuUsageUtilization = (totalCPUUsage / totalNodeCPU) * 100;
    const memoryRequestUtilization = (totalMemoryRequest / totalNodeMemory) * 100;
    const memoryUsageUtilization = (totalMemoryUsage / totalNodeMemory) * 100;

    // Cost calculations
    const currentMonthlyCost = nodePool.count * nodePool.costPerHour * 24 * 30;
    const spotMonthlyCost = useSpotInstances
      ? currentMonthlyCost * (1 - spotSavingsPercent / 100)
      : currentMonthlyCost;

    // Right-sizing recommendations
    const rightSizedPods = validPods.map(pod => {
      const overProvisionedCPU = pod.cpuRequest > pod.cpuUsage * 1.2;
      const overProvisionedMemory = pod.memoryRequest > pod.memoryUsage * 1.2;

      const recommendedCPU = overProvisionedCPU
        ? Math.ceil(pod.cpuUsage * 1.2 / 100) * 100 // 20% buffer, round up to nearest 100
        : pod.cpuRequest;

      const recommendedMemory = overProvisionedMemory
        ? Math.ceil(pod.memoryUsage * 1.2 / 128) * 128 // 20% buffer, round up to nearest 128MB
        : pod.memoryRequest;

      const cpuSavings = (pod.cpuRequest - recommendedCPU) * pod.replicas;
      const memorySavings = (pod.memoryRequest - recommendedMemory) * pod.replicas;

      return {
        ...pod,
        recommendedCPU,
        recommendedMemory,
        overProvisionedCPU,
        overProvisionedMemory,
        cpuSavings,
        memorySavings,
      };
    });

    const totalCPUSavings = rightSizedPods.reduce((sum, p) => sum + p.cpuSavings, 0);
    const totalMemorySavings = rightSizedPods.reduce((sum, p) => sum + p.memorySavings, 0);

    // Optimized node count
    const optimizedCPURequest = totalCPURequest - totalCPUSavings;
    const optimizedMemoryRequest = totalMemoryRequest - totalMemorySavings;

    const nodesNeededForCPU = Math.ceil(optimizedCPURequest / (nodePool.vCPUs * 1000));
    const nodesNeededForMemory = Math.ceil(optimizedMemoryRequest / (nodePool.memoryGB * 1024));
    const optimizedNodeCount = Math.max(nodesNeededForCPU, nodesNeededForMemory, 2); // Minimum 2 for HA

    const optimizedMonthlyCost = optimizedNodeCount * nodePool.costPerHour * 24 * 30;
    const savingsFromRightSizing = currentMonthlyCost - optimizedMonthlyCost;
    const totalSavings = useSpotInstances
      ? savingsFromRightSizing + (optimizedMonthlyCost * spotSavingsPercent / 100)
      : savingsFromRightSizing;

    // Namespace breakdown
    const namespaces = [...new Set(validPods.map(p => p.namespace))];
    const namespaceStats = namespaces.map(ns => {
      const nsPods = validPods.filter(p => p.namespace === ns);
      return {
        namespace: ns,
        pods: nsPods.length,
        cpuRequest: nsPods.reduce((sum, p) => sum + (p.cpuRequest * p.replicas), 0),
        memoryRequest: nsPods.reduce((sum, p) => sum + (p.memoryRequest * p.replicas), 0),
      };
    });

    return {
      totalCPURequest,
      totalCPUUsage,
      totalMemoryRequest,
      totalMemoryUsage,
      totalNodeCPU,
      totalNodeMemory,
      cpuRequestUtilization,
      cpuUsageUtilization,
      memoryRequestUtilization,
      memoryUsageUtilization,
      currentMonthlyCost,
      spotMonthlyCost,
      rightSizedPods,
      totalCPUSavings,
      totalMemorySavings,
      optimizedNodeCount,
      optimizedMonthlyCost,
      savingsFromRightSizing,
      totalSavings,
      namespaceStats,
      validPods,
    };
  }, [pods, nodePool, useSpotInstances, spotSavingsPercent]);

  // Pod functions
  const addPod = () => {
    setPods([
      ...pods,
      { id: Date.now().toString(), name: '', namespace: 'default', cpuRequest: 100, cpuUsage: 50, memoryRequest: 128, memoryUsage: 64, replicas: 1 },
    ]);
  };

  const updatePod = (id: string, field: keyof Pod, value: string | number) => {
    setPods(pods.map(p => p.id === id ? { ...p, [field]: value } : p));
  };

  const removePod = (id: string) => {
    setPods(pods.filter(p => p.id !== id));
  };

  // Export to Excel
  const exportToExcel = () => {
    if (typeof window === 'undefined') return;

    const script = document.createElement('script');
    script.src = 'https://cdn.sheetjs.com/xlsx-0.20.3/package/dist/xlsx.full.min.js';
    script.onload = () => {
      const XLSX = (window as any).XLSX;
      const wb = XLSX.utils.book_new();

      // Sheet 1: Summary
      const summaryData = [
        ['Kubernetes Cost Optimization', ''],
        ['', ''],
        ['Current State', ''],
        ['Node Count', nodePool.count],
        ['Node Type', nodePool.instanceType],
        ['Total vCPUs', nodePool.count * nodePool.vCPUs],
        ['Total Memory (GB)', nodePool.count * nodePool.memoryGB],
        ['Monthly Cost', '$' + calculations.currentMonthlyCost.toFixed(2)],
        ['', ''],
        ['Utilization', ''],
        ['CPU Request Utilization', calculations.cpuRequestUtilization.toFixed(1) + '%'],
        ['CPU Usage Utilization', calculations.cpuUsageUtilization.toFixed(1) + '%'],
        ['Memory Request Utilization', calculations.memoryRequestUtilization.toFixed(1) + '%'],
        ['Memory Usage Utilization', calculations.memoryUsageUtilization.toFixed(1) + '%'],
        ['', ''],
        ['Optimization', ''],
        ['Recommended Node Count', calculations.optimizedNodeCount],
        ['Optimized Monthly Cost', '$' + calculations.optimizedMonthlyCost.toFixed(2)],
        ['Savings from Right-sizing', '$' + calculations.savingsFromRightSizing.toFixed(2)],
        ['With Spot Instances', useSpotInstances ? 'Yes' : 'No'],
        ['Total Monthly Savings', '$' + calculations.totalSavings.toFixed(2)],
        ['Annual Savings', '$' + (calculations.totalSavings * 12).toFixed(2)],
      ];

      const ws1 = XLSX.utils.aoa_to_sheet(summaryData);
      ws1['!cols'] = [{ wch: 30 }, { wch: 20 }];
      XLSX.utils.book_append_sheet(wb, ws1, 'Summary');

      // Sheet 2: Right-sizing Recommendations
      const rightSizingData = calculations.rightSizedPods.map(p => ({
        'Pod': p.name,
        'Namespace': p.namespace,
        'Replicas': p.replicas,
        'Current CPU': p.cpuRequest + 'm',
        'Recommended CPU': p.recommendedCPU + 'm',
        'CPU Savings': p.cpuSavings + 'm',
        'Current Memory': p.memoryRequest + 'MB',
        'Recommended Memory': p.recommendedMemory + 'MB',
        'Memory Savings': p.memorySavings + 'MB',
      }));

      const ws2 = XLSX.utils.json_to_sheet(rightSizingData);
      ws2['!cols'] = [{ wch: 20 }, { wch: 15 }, { wch: 10 }, { wch: 15 }, { wch: 18 }, { wch: 15 }, { wch: 18 }, { wch: 22 }, { wch: 18 }];
      XLSX.utils.book_append_sheet(wb, ws2, 'Right-sizing');

      // Sheet 3: Namespace Breakdown
      const namespaceData = calculations.namespaceStats.map(ns => ({
        'Namespace': ns.namespace,
        'Pods': ns.pods,
        'CPU Request': ns.cpuRequest + 'm',
        'Memory Request': ns.memoryRequest + 'MB',
      }));

      const ws3 = XLSX.utils.json_to_sheet(namespaceData);
      ws3['!cols'] = [{ wch: 20 }, { wch: 10 }, { wch: 15 }, { wch: 18 }];
      XLSX.utils.book_append_sheet(wb, ws3, 'Namespaces');

      (wb.Workbook as any) = { CalcPr: { fullCalcOnLoad: true } };
      XLSX.writeFile(wb, 'kubernetes-cost-optimization.xlsx');
    };
    document.head.appendChild(script);
  };

  // Save to Command Center
  const handleSaveToCommandCenter = async () => {
    if (!commandCenterEnabled) return;

    const overProvisionedPods = calculations.rightSizedPods.filter(
      (p) => p.overProvisionedCPU || p.overProvisionedMemory
    );

    const gaps = overProvisionedPods.map((pod) => {
      const severity: 'critical' | 'high' | 'medium' | 'low' = pod.cpuSavings > 500 || pod.memorySavings > 512 ? 'high' : 'medium';
      return {
        id: pod.id,
        title: `${pod.name} - Over-provisioned Resources`,
        severity,
        description: `CPU: ${pod.cpuRequest}m -> ${pod.recommendedCPU}m, Memory: ${pod.memoryRequest}MB -> ${pod.recommendedMemory}MB`,
        recommendation: `Reduce resource requests to save ${pod.cpuSavings}m CPU and ${pod.memorySavings}MB memory across ${pod.replicas} replicas`,
      };
    });

    const recommendations: Array<{
      id: string;
      title: string;
      priority: 'high' | 'medium' | 'low';
      description: string;
      effort: string;
    }> = [
      {
        id: 'right-sizing',
        title: 'Right-size Pod Resources',
        priority: 'high',
        description: `Potential savings: ${calculations.totalCPUSavings}m CPU, ${calculations.totalMemorySavings}MB memory`,
        effort: 'Low - Update resource requests in deployments',
      },
      {
        id: 'node-count',
        title: `Optimize Node Count (${nodePool.count} -> ${calculations.optimizedNodeCount})`,
        priority: calculations.optimizedNodeCount < nodePool.count ? 'high' : 'low',
        description: `Monthly savings from node optimization: $${calculations.savingsFromRightSizing.toFixed(2)}`,
        effort: 'Medium - Scale down node pool',
      },
    ];

    if (useSpotInstances) {
      recommendations.push({
        id: 'spot-instances',
        title: 'Use Spot Instances',
        priority: 'medium',
        description: `Expected ${spotSavingsPercent}% savings on compute costs`,
        effort: 'Medium - Configure spot instance node pools',
      });
    }

    const assessment = await saveResult({
      name: `K8s Cost Analysis - ${nodePool.name} - ${new Date().toLocaleDateString()}`,
      score_overall: Math.round((calculations.totalSavings / calculations.currentMonthlyCost) * 100),
      scores: {
        'CPU Request Utilization': Math.round(calculations.cpuRequestUtilization),
        'CPU Usage Utilization': Math.round(calculations.cpuUsageUtilization),
        'Memory Request Utilization': Math.round(calculations.memoryRequestUtilization),
        'Memory Usage Utilization': Math.round(calculations.memoryUsageUtilization),
        'Current Monthly Cost': calculations.currentMonthlyCost,
        'Optimized Monthly Cost': calculations.optimizedMonthlyCost,
        'Total Monthly Savings': calculations.totalSavings,
      },
      answers: {
        nodePool: {
          name: nodePool.name,
          instanceType: nodePool.instanceType,
          vCPUs: nodePool.vCPUs,
          memoryGB: nodePool.memoryGB,
          costPerHour: nodePool.costPerHour,
          count: nodePool.count,
        },
        pods: calculations.validPods.map((p) => ({
          name: p.name,
          namespace: p.namespace,
          cpuRequest: p.cpuRequest,
          cpuUsage: p.cpuUsage,
          memoryRequest: p.memoryRequest,
          memoryUsage: p.memoryUsage,
          replicas: p.replicas,
        })),
        useSpotInstances,
        spotSavingsPercent,
      },
      gaps,
      recommendations,
      metadata: {
        clusterCapacity: {
          totalCPU: calculations.totalNodeCPU,
          totalMemory: calculations.totalNodeMemory,
        },
        resourceUsage: {
          cpuRequest: calculations.totalCPURequest,
          cpuUsage: calculations.totalCPUUsage,
          memoryRequest: calculations.totalMemoryRequest,
          memoryUsage: calculations.totalMemoryUsage,
        },
        optimization: {
          currentNodeCount: nodePool.count,
          optimizedNodeCount: calculations.optimizedNodeCount,
          cpuSavings: calculations.totalCPUSavings,
          memorySavings: calculations.totalMemorySavings,
        },
        costs: {
          current: calculations.currentMonthlyCost,
          optimized: calculations.optimizedMonthlyCost,
          savings: calculations.totalSavings,
          annualSavings: calculations.totalSavings * 12,
        },
        namespaces: calculations.namespaceStats,
      },
    });

    if (assessment) {
      setSaved(true);
    }
  };

  const getUtilizationColor = (percent: number) => {
    if (percent > 80) return 'text-red-500';
    if (percent > 60) return 'text-accent';
    if (percent > 40) return 'text-yellow-500';
    return 'text-green-500';
  };

  return (
    <div className="container mx-auto px-4 py-12">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <Link href="/tools" className="text-accent hover:text-accent mb-4 inline-block">
            ← Back to Tools
          </Link>
          <h1 className="text-4xl font-bold mb-4 text-light-text dark:text-foreground">
            Kubernetes Cost Optimizer
          </h1>
          <p className="text-xl text-light-text-secondary dark:text-foreground-secondary">
            Analyze resource requests vs usage, get right-sizing recommendations, optimize node pools,
            and calculate spot instance savings for your Kubernetes clusters.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Left Column: Configuration */}
          <div className="space-y-6">
            {/* Node Pool */}
            <div className="bg-white dark:bg-surface border border-gray-300 dark:border-border rounded-lg p-6">
              <h2 className="text-2xl font-bold mb-4 text-light-text dark:text-foreground">
                Node Pool Configuration
              </h2>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2 text-light-text dark:text-foreground">
                    Instance Type
                  </label>
                  <select
                    value={nodePool.instanceType}
                    onChange={(e) => {
                      const type = e.target.value;
                      const specs = {
                        't3.large': { vCPUs: 2, memoryGB: 8, costPerHour: 0.0832 },
                        't3.xlarge': { vCPUs: 4, memoryGB: 16, costPerHour: 0.1664 },
                        't3.2xlarge': { vCPUs: 8, memoryGB: 32, costPerHour: 0.3328 },
                        'm5.large': { vCPUs: 2, memoryGB: 8, costPerHour: 0.096 },
                        'm5.xlarge': { vCPUs: 4, memoryGB: 16, costPerHour: 0.192 },
                        'm5.2xlarge': { vCPUs: 8, memoryGB: 32, costPerHour: 0.384 },
                        'c5.large': { vCPUs: 2, memoryGB: 4, costPerHour: 0.085 },
                        'c5.xlarge': { vCPUs: 4, memoryGB: 8, costPerHour: 0.17 },
                      }[type] || { vCPUs: 2, memoryGB: 8, costPerHour: 0.0832 };

                      setNodePool({ ...nodePool, instanceType: type, ...specs });
                    }}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-border rounded-lg bg-white dark:bg-background text-light-text dark:text-foreground"
                  >
                    <option value="t3.large">t3.large (2 vCPU, 8GB)</option>
                    <option value="t3.xlarge">t3.xlarge (4 vCPU, 16GB)</option>
                    <option value="t3.2xlarge">t3.2xlarge (8 vCPU, 32GB)</option>
                    <option value="m5.large">m5.large (2 vCPU, 8GB)</option>
                    <option value="m5.xlarge">m5.xlarge (4 vCPU, 16GB)</option>
                    <option value="m5.2xlarge">m5.2xlarge (8 vCPU, 32GB)</option>
                    <option value="c5.large">c5.large (2 vCPU, 4GB)</option>
                    <option value="c5.xlarge">c5.xlarge (4 vCPU, 8GB)</option>
                  </select>
                  <p className="text-xs text-light-text-secondary dark:text-foreground-tertiary mt-1">
                    ${nodePool.costPerHour.toFixed(4)}/hour
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2 text-light-text dark:text-foreground">
                    Node Count
                  </label>
                  <input
                    type="number" inputMode="numeric"
                    value={nodePool.count}
                    onChange={(e) => setNodePool({ ...nodePool, count: Math.max(1, parseInt(e.target.value) || 1) })}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-border rounded-lg bg-white dark:bg-background text-light-text dark:text-foreground"
                  />
                </div>

                <div className="pt-4 border-t border-gray-300 dark:border-border">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <div className="text-light-text-secondary dark:text-foreground-tertiary">Total vCPUs</div>
                      <div className="text-lg font-bold text-light-text dark:text-foreground">
                        {nodePool.count * nodePool.vCPUs}
                      </div>
                    </div>
                    <div>
                      <div className="text-light-text-secondary dark:text-foreground-tertiary">Total Memory</div>
                      <div className="text-lg font-bold text-light-text dark:text-foreground">
                        {nodePool.count * nodePool.memoryGB} GB
                      </div>
                    </div>
                    <div className="col-span-2">
                      <div className="text-light-text-secondary dark:text-foreground-tertiary">Monthly Cost</div>
                      <div className="text-2xl font-bold text-accent">
                        ${calculations.currentMonthlyCost.toFixed(2)}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Spot Instances */}
            <div className="bg-white dark:bg-surface border border-gray-300 dark:border-border rounded-lg p-6">
              <h2 className="text-2xl font-bold mb-4 text-light-text dark:text-foreground">
                Spot Instance Savings
              </h2>

              <div className="space-y-4">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={useSpotInstances}
                    onChange={(e) => setUseSpotInstances(e.target.checked)}
                    className="w-5 h-5"
                  />
                  <span className="text-light-text dark:text-foreground">Use Spot Instances</span>
                </label>

                {useSpotInstances && (
                  <>
                    <div>
                      <label className="block text-sm font-medium mb-2 text-light-text dark:text-foreground">
                        Expected Savings: {spotSavingsPercent}%
                      </label>
                      <input
                        type="range"
                        min="50"
                        max="90"
                        step="5"
                        value={spotSavingsPercent}
                        onChange={(e) => setSpotSavingsPercent(parseInt(e.target.value))}
                        className="w-full"
                      />
                      <div className="flex justify-between text-xs text-light-text-secondary dark:text-foreground-tertiary">
                        <span>50%</span>
                        <span>90%</span>
                      </div>
                    </div>

                    <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
                      <div className="text-sm text-green-500 mb-1">Spot Instance Savings</div>
                      <div className="text-2xl font-bold text-green-500">
                        ${(calculations.currentMonthlyCost * spotSavingsPercent / 100).toFixed(2)}/mo
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Pods */}
            <div className="bg-white dark:bg-surface border border-gray-300 dark:border-border rounded-lg p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-2xl font-bold text-light-text dark:text-foreground">
                  Pods ({calculations.validPods.length})
                </h2>
                <button
                  onClick={addPod}
                  className="px-4 py-2 bg-accent text-accent-foreground font-semibold rounded-lg hover:bg-accent-hover transition-colors text-sm"
                >
                  + Add Pod
                </button>
              </div>

              <div className="space-y-3 max-h-[400px] overflow-y-auto">
                {pods.map((pod) => (
                  <div key={pod.id} className="border border-gray-300 dark:border-border rounded-lg p-4">
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex-1 grid grid-cols-2 gap-2">
                        <input
                          type="text"
                          placeholder="Pod name"
                          value={pod.name}
                          onChange={(e) => updatePod(pod.id, 'name', e.target.value)}
                          className="px-3 py-2 border border-gray-300 dark:border-border rounded-lg bg-white dark:bg-background text-light-text dark:text-foreground text-sm"
                        />
                        <input
                          type="text"
                          placeholder="Namespace"
                          value={pod.namespace}
                          onChange={(e) => updatePod(pod.id, 'namespace', e.target.value)}
                          className="px-3 py-2 border border-gray-300 dark:border-border rounded-lg bg-white dark:bg-background text-light-text dark:text-foreground text-sm"
                        />
                      </div>
                      <button
                        onClick={() => removePod(pod.id)}
                        className="ml-2 text-red-500 hover:text-red-600"
                      >
                        ✕
                      </button>
                    </div>

                    <div className="grid grid-cols-3 gap-2 mb-2">
                      <div>
                        <label className="block text-xs text-light-text-secondary dark:text-foreground-tertiary mb-1">
                          CPU Request (m)
                        </label>
                        <input
                          type="number" inputMode="numeric"
                          value={pod.cpuRequest}
                          onChange={(e) => updatePod(pod.id, 'cpuRequest', parseInt(e.target.value) || 0)}
                          className="w-full px-2 py-1 border border-gray-300 dark:border-border rounded bg-white dark:bg-background text-light-text dark:text-foreground text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-light-text-secondary dark:text-foreground-tertiary mb-1">
                          CPU Usage (m)
                        </label>
                        <input
                          type="number" inputMode="numeric"
                          value={pod.cpuUsage}
                          onChange={(e) => updatePod(pod.id, 'cpuUsage', parseInt(e.target.value) || 0)}
                          className="w-full px-2 py-1 border border-gray-300 dark:border-border rounded bg-white dark:bg-background text-light-text dark:text-foreground text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-light-text-secondary dark:text-foreground-tertiary mb-1">
                          Replicas
                        </label>
                        <input
                          type="number" inputMode="numeric"
                          value={pod.replicas}
                          onChange={(e) => updatePod(pod.id, 'replicas', Math.max(1, parseInt(e.target.value) || 1))}
                          className="w-full px-2 py-1 border border-gray-300 dark:border-border rounded bg-white dark:bg-background text-light-text dark:text-foreground text-sm"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-xs text-light-text-secondary dark:text-foreground-tertiary mb-1">
                          Memory Request (MB)
                        </label>
                        <input
                          type="number" inputMode="numeric"
                          value={pod.memoryRequest}
                          onChange={(e) => updatePod(pod.id, 'memoryRequest', parseInt(e.target.value) || 0)}
                          className="w-full px-2 py-1 border border-gray-300 dark:border-border rounded bg-white dark:bg-background text-light-text dark:text-foreground text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-light-text-secondary dark:text-foreground-tertiary mb-1">
                          Memory Usage (MB)
                        </label>
                        <input
                          type="number" inputMode="numeric"
                          value={pod.memoryUsage}
                          onChange={(e) => updatePod(pod.id, 'memoryUsage', parseInt(e.target.value) || 0)}
                          className="w-full px-2 py-1 border border-gray-300 dark:border-border rounded bg-white dark:bg-background text-light-text dark:text-foreground text-sm"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right Column: Analysis */}
          <div className="space-y-6">
            {/* Utilization */}
            <div className="bg-white dark:bg-surface border border-gray-300 dark:border-border rounded-lg p-6">
              <h2 className="text-2xl font-bold mb-4 text-light-text dark:text-foreground">
                Cluster Utilization
              </h2>

              <div className="space-y-4">
                <div>
                  <div className="flex justify-between mb-2">
                    <span className="text-sm text-light-text dark:text-foreground">CPU Request</span>
                    <span className={`text-sm font-bold ${getUtilizationColor(calculations.cpuRequestUtilization)}`}>
                      {calculations.cpuRequestUtilization.toFixed(1)}%
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-background rounded-full h-3">
                    <div
                      className={`h-3 rounded-full ${
                        calculations.cpuRequestUtilization > 80 ? 'bg-red-500' :
                        calculations.cpuRequestUtilization > 60 ? 'bg-accent' :
                        'bg-green-500'
                      }`}
                      style={{ width: `${Math.min(100, calculations.cpuRequestUtilization)}%` }}
                    ></div>
                  </div>
                </div>

                <div>
                  <div className="flex justify-between mb-2">
                    <span className="text-sm text-light-text dark:text-foreground">CPU Usage</span>
                    <span className={`text-sm font-bold ${getUtilizationColor(calculations.cpuUsageUtilization)}`}>
                      {calculations.cpuUsageUtilization.toFixed(1)}%
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-background rounded-full h-3">
                    <div
                      className="h-3 rounded-full bg-blue-500"
                      style={{ width: `${Math.min(100, calculations.cpuUsageUtilization)}%` }}
                    ></div>
                  </div>
                </div>

                <div>
                  <div className="flex justify-between mb-2">
                    <span className="text-sm text-light-text dark:text-foreground">Memory Request</span>
                    <span className={`text-sm font-bold ${getUtilizationColor(calculations.memoryRequestUtilization)}`}>
                      {calculations.memoryRequestUtilization.toFixed(1)}%
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-background rounded-full h-3">
                    <div
                      className={`h-3 rounded-full ${
                        calculations.memoryRequestUtilization > 80 ? 'bg-red-500' :
                        calculations.memoryRequestUtilization > 60 ? 'bg-accent' :
                        'bg-green-500'
                      }`}
                      style={{ width: `${Math.min(100, calculations.memoryRequestUtilization)}%` }}
                    ></div>
                  </div>
                </div>

                <div>
                  <div className="flex justify-between mb-2">
                    <span className="text-sm text-light-text dark:text-foreground">Memory Usage</span>
                    <span className={`text-sm font-bold ${getUtilizationColor(calculations.memoryUsageUtilization)}`}>
                      {calculations.memoryUsageUtilization.toFixed(1)}%
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-background rounded-full h-3">
                    <div
                      className="h-3 rounded-full bg-blue-500"
                      style={{ width: `${Math.min(100, calculations.memoryUsageUtilization)}%` }}
                    ></div>
                  </div>
                </div>
              </div>
            </div>

            {/* Cost Optimization */}
            <div className="bg-gradient-to-r from-green-500/10 to-green-500/5 border border-green-500/30 rounded-lg p-6">
              <h2 className="text-2xl font-bold mb-4 text-light-text dark:text-foreground">
                Cost Optimization
              </h2>

              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="bg-white dark:bg-surface rounded-lg p-4">
                  <div className="text-xs text-light-text-secondary dark:text-foreground-tertiary mb-1">
                    Current Cost
                  </div>
                  <div className="text-2xl font-bold text-light-text dark:text-foreground">
                    ${calculations.currentMonthlyCost.toFixed(0)}
                  </div>
                  <div className="text-xs text-light-text-secondary dark:text-foreground-tertiary">per month</div>
                </div>

                <div className="bg-white dark:bg-surface rounded-lg p-4">
                  <div className="text-xs text-light-text-secondary dark:text-foreground-tertiary mb-1">
                    Optimized Cost
                  </div>
                  <div className="text-2xl font-bold text-green-500">
                    ${calculations.optimizedMonthlyCost.toFixed(0)}
                  </div>
                  <div className="text-xs text-light-text-secondary dark:text-foreground-tertiary">per month</div>
                </div>

                <div className="col-span-2 bg-green-500/20 border border-green-500/40 rounded-lg p-4">
                  <div className="text-sm text-green-600 dark:text-green-500 mb-1">
                    Total Monthly Savings
                  </div>
                  <div className="text-3xl font-bold text-green-600 dark:text-green-500">
                    ${calculations.totalSavings.toFixed(2)}
                  </div>
                  <div className="text-xs text-green-600 dark:text-green-500 mt-1">
                    ${(calculations.totalSavings * 12).toFixed(2)} annual savings
                  </div>
                </div>
              </div>

              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-light-text-secondary dark:text-foreground-secondary">Node Count</span>
                  <span className="font-semibold text-light-text dark:text-foreground">
                    {nodePool.count} → {calculations.optimizedNodeCount}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-light-text-secondary dark:text-foreground-secondary">Right-sizing Savings</span>
                  <span className="font-semibold text-green-500">
                    ${calculations.savingsFromRightSizing.toFixed(2)}
                  </span>
                </div>
                {useSpotInstances && (
                  <div className="flex justify-between">
                    <span className="text-light-text-secondary dark:text-foreground-secondary">Spot Instance Savings</span>
                    <span className="font-semibold text-green-500">
                      ${(calculations.optimizedMonthlyCost * spotSavingsPercent / 100).toFixed(2)}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Right-sizing Recommendations */}
            <div className="bg-white dark:bg-surface border border-gray-300 dark:border-border rounded-lg p-6">
              <h2 className="text-2xl font-bold mb-4 text-light-text dark:text-foreground">
                Right-sizing Recommendations
              </h2>

              {calculations.rightSizedPods.filter(p => p.overProvisionedCPU || p.overProvisionedMemory).length > 0 ? (
                <div className="space-y-3 max-h-[300px] overflow-y-auto">
                  {calculations.rightSizedPods
                    .filter(p => p.overProvisionedCPU || p.overProvisionedMemory)
                    .map(pod => (
                      <div key={pod.id} className="border border-border bg-accent-muted rounded-lg p-3">
                        <div className="font-semibold text-light-text dark:text-foreground mb-2">
                          {pod.name}
                        </div>
                        {pod.overProvisionedCPU && (
                          <div className="text-xs text-light-text-secondary dark:text-foreground-secondary mb-1">
                            <span className="font-semibold">CPU:</span> {pod.cpuRequest}m → {pod.recommendedCPU}m
                            <span className="text-green-500 ml-2">(-{pod.cpuSavings}m total)</span>
                          </div>
                        )}
                        {pod.overProvisionedMemory && (
                          <div className="text-xs text-light-text-secondary dark:text-foreground-secondary">
                            <span className="font-semibold">Memory:</span> {pod.memoryRequest}MB → {pod.recommendedMemory}MB
                            <span className="text-green-500 ml-2">(-{pod.memorySavings}MB total)</span>
                          </div>
                        )}
                      </div>
                    ))}
                </div>
              ) : (
                <div className="text-center py-6">
                  <div className="text-3xl mb-2">✅</div>
                  <p className="text-light-text-secondary dark:text-foreground-secondary text-sm">
                    All pods are efficiently sized!
                  </p>
                </div>
              )}
            </div>

            {/* Namespace Breakdown */}
            <div className="bg-white dark:bg-surface border border-gray-300 dark:border-border rounded-lg p-6">
              <h2 className="text-2xl font-bold mb-4 text-light-text dark:text-foreground">
                Namespace Breakdown
              </h2>

              <div className="space-y-2">
                {calculations.namespaceStats.map(ns => (
                  <div key={ns.namespace} className="flex justify-between items-center py-2 border-b border-gray-300 dark:border-border last:border-0">
                    <span className="font-semibold text-light-text dark:text-foreground">{ns.namespace}</span>
                    <div className="text-right text-sm">
                      <div className="text-light-text-secondary dark:text-foreground-secondary">
                        {ns.pods} pods
                      </div>
                      <div className="text-xs text-light-text-secondary dark:text-foreground-tertiary">
                        {ns.cpuRequest}m CPU, {ns.memoryRequest}MB RAM
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Export */}
            <button
              onClick={exportToExcel}
              className="w-full px-6 py-3 bg-accent text-accent-foreground font-semibold rounded-lg hover:bg-accent-hover transition-colors"
            >
              Export Cost Analysis to Excel
            </button>

            {/* Command Center Save Button */}
            <div className="flex flex-wrap gap-4">
              {commandCenterEnabled && !saved && (
                <button
                  onClick={handleSaveToCommandCenter}
                  disabled={saving}
                  className="w-full px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving ? 'Saving...' : 'Save to Command Center'}
                </button>
              )}
              {saved && (
                <div className="w-full px-6 py-3 bg-green-600/20 text-green-400 rounded-lg font-medium flex items-center justify-center gap-2">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Saved to Command Center
                </div>
              )}
              {saveError && (
                <div className="w-full px-6 py-3 bg-red-600/20 text-red-400 rounded-lg text-sm text-center">
                  {saveError}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Pro Features */}
        <div className="mt-8 bg-gradient-to-r from-accent-muted to-accent/5 border-l-4 border-accent p-6 rounded-lg">
          <h3 className="text-xl font-bold text-accent mb-3">⭐ Unlock Pro Features</h3>
          <div className="grid md:grid-cols-2 gap-4 mb-4">
            <div>
              <div className="font-semibold text-light-text dark:text-foreground mb-2">Pro ($29/month):</div>
              <ul className="space-y-1 text-sm text-light-text-secondary dark:text-foreground-secondary">
                <li>✓ Multi-cluster support</li>
                <li>✓ Auto-scaling policy recommendations</li>
                <li>✓ Node pool optimization</li>
                <li>✓ Excel export with savings analysis</li>
                <li>✓ Historical trend analysis</li>
              </ul>
            </div>
            <div>
              <div className="font-semibold text-light-text dark:text-foreground mb-2">Team ($99/month):</div>
              <ul className="space-y-1 text-sm text-light-text-secondary dark:text-foreground-secondary">
                <li>✓ Everything in Pro</li>
                <li>✓ Real-time cost monitoring</li>
                <li>✓ Auto-discovery from K8s clusters</li>
                <li>✓ Multi-cluster comparison</li>
                <li>✓ FinOps report generation</li>
              </ul>
            </div>
          </div>
          <Link
            href="/pricing"
            className="inline-block px-6 py-2 bg-accent text-accent-foreground font-semibold rounded-lg hover:bg-accent-hover transition-colors"
          >
            Upgrade to Pro →
          </Link>
        </div>
      </div>
    </div>
  );
}
